import React, { useState, SyntheticEvent, useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useQuery } from "react-query";
import { isEmpty } from "lodash";
import CommunicationApi from "../services/API/Communication.Api";

import { Box } from "@mui/system";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Button, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Typography } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import MySnackbarContentWrapper from "../MySnackbarContentWrapper";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import CircularProgress from "@mui/material/CircularProgress";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const validationSchema = yup.object({
  messageTitle: yup.string().required("Message Title is required"),
  messageBody: yup.string().required("Message Body is required"),
});

function Communication() {
  const [hostpoolState, setHostPoolState] = useState({
    hostPool: "",
    resourceGroup: "",
  });
  const [options, setOptions] = useState([] as any);
  const [selected, setSelected] = useState([] as any);
  const [users, setUsers] = useState([] as any);
  const [filteredUsers, setFilteredUsers] = useState([] as any);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [output, SetOutput] = useState("" as string);
  const [openSnackBar, setOpenSnackbar] = useState<boolean>(false);
  const [isSendMessage, setIsSendMessage] = useState<boolean>(false);

  const isAllSelected =
    options.length > 0 && selected.length === options.length;

  //!---------------------- FORMIK ----------------------//
  const formik = useFormik({
    initialValues: {
      messageTitle: "",
      messageBody: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSendMessage(true);
        let res = await CommunicationApi.notifyUser({
          ...values,
          ...hostpoolState,
          userList: filteredUsers,
        });

        // Reset Form after success
        setHostPoolState({ hostPool: "", resourceGroup: "" });
        setSelected([]);
        setFilteredUsers([]);
        formik.resetForm();
        setIsSendMessage(false);

        // Show success toaster
        SetOutput("success");
        setOpenSnackbar(true);
      } catch (error) {
        // Reset Form after failure
        setHostPoolState({ hostPool: "", resourceGroup: "" });
        setSelected([]);
        setFilteredUsers([]);
        formik.resetForm();
        setIsSendMessage(false);

        // Show failure toaster
        SetOutput("failure");
        setOpenSnackbar(true);
      }
    },
  });

  //!----------------- Fetching Host Pools -----------------//
  const { data: hostPools, isFetching: isHostPoolFetching } = useQuery(
    "GetHostPool",
    () => CommunicationApi.getHostPool(),
    {
      initialData: [],
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  //!----------------- Fetching Session Hosts By Host Pool -----------------//
  const { data: sessionHosts, isFetching: isSessionHostFetching } = useQuery(
    ["GetSessionHosts", hostpoolState.hostPool],
    () => CommunicationApi.getSessionHost(hostpoolState),
    {
      initialData: [],
      refetchOnWindowFocus: false,
      enabled: !isEmpty(hostpoolState.hostPool),
    }
  );

  // SessionHosts Data Filtering:
  useEffect(() => {
    let filteredSessionHosts = sessionHosts.map((item: any) => {
      let strArray = item.name.split("/");
      item.name = strArray[1];
      return item;
    });

    const data = filteredSessionHosts.map((item: any) => item.name);
    setOptions(data);
  }, [sessionHosts]);

  //!---------------------- Handlers ----------------------//
  const handlehostPool = (item: any) => {
    let idString = item.id;
    let resultArray = idString.split("/");
    let resourceGroupName = resultArray[4];

    setHostPoolState({
      ...hostpoolState,
      hostPool: item.name,
      resourceGroup: resourceGroupName,
    });
  };

  // Reset SessionHost & UsersData when select new HostPool:
  useEffect(() => {
    setSelected([]);
    setFilteredUsers([]);
  }, [hostpoolState]);

  const handleSessionHost = (event: any) => {
    const value = event.target.value;
    if (value[value.length - 1] === "all") {
      setSelected(selected.length === options.length ? [] : options);
      return;
    }
    setSelected(value);
  };

  const isFormValid = () => {
    return hostpoolState.hostPool && selected && filteredUsers.length > 0;
  };

  const handleClose = (event?: SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  //!-------------- Fetching User Session By Session Host --------------//
  const handleGetUsers = async () => {
    try {
      setIsLoading(true);
      let response = await CommunicationApi.getUserSessions({
        ...hostpoolState,
        sessionHostList: selected,
      });
      setUsers(response);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  // Filter Users Data:
  useEffect(() => {
    let simplifiedArray = users.map((item: any) =>
      item.value.map((item2: any) => {
        let nameArray = item2?.name?.split("/");
        let splitData = nameArray[1].split(".");
        return {
          sessionHostName: splitData[0],
          userSessionId: nameArray[2],
          userName: item2?.properties?.userPrincipalName,
        };
      })
    );
    const newArr = simplifiedArray.flat();
    setFilteredUsers(newArr);
  }, [users]);

  return (
    <Box sx={{ width: "100%" }}>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSnackBar}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        {output === "success" ? (
          <MySnackbarContentWrapper
            onClose={handleClose}
            variant="success"
            message="Notify User Successfully"
          />
        ) : (
          <MySnackbarContentWrapper
            onClose={handleClose}
            variant="error"
            message="Something Went Wrong!"
          />
        )}
      </Snackbar>

      <Box sx={{ margin: "30px 0 20px 50px" }}>
        <Typography variant="h5">Notify User</Typography>
      </Box>

      <Grid container>
        {/**************** Left Grid ****************/}
        <Grid
          item
          xs={6}
          sx={{
            padding: "20px 50px 20px 50px",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ marginBottom: "20px" }}>
            <FormControl fullWidth>
              <InputLabel>Select Host Pool</InputLabel>
              <Select
                name="hostPool"
                value={hostpoolState.hostPool}
                label="Select Host Pool"
              >
                {isHostPoolFetching ? (
                  <MenuItem>Loading...</MenuItem>
                ) : (
                  hostPools &&
                  hostPools.map((item: any) => (
                    <MenuItem
                      key={item.name}
                      value={item.name}
                      onClick={(e) => handlehostPool(item)}
                    >
                      {item.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ marginBottom: "20px" }}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel id="demo-multiple-checkbox-label">
                Select Session Host
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selected}
                onChange={handleSessionHost}
                input={<OutlinedInput label="Select Session Host" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
                disabled={isEmpty(hostpoolState.hostPool)}
              >
                <MenuItem value="all">
                  <ListItemIcon>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selected.length > 0 && selected.length < options.length
                      }
                    />
                  </ListItemIcon>
                  <ListItemText primary="Select All" />
                </MenuItem>

                {options.map((option: any) => (
                  <MenuItem key={option} value={option}>
                    <ListItemIcon>
                      <Checkbox checked={selected.indexOf(option) > -1} />
                    </ListItemIcon>
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isLoading ? (
            <Box>
              <Button
                disabled
                fullWidth
                variant="contained"
                sx={{ mb: 3 }}
                endIcon={<CircularProgress size={20} />}
              >
                Get Users
              </Button>
            </Box>
          ) : (
            <Box>
              <Button
                fullWidth
                variant="contained"
                sx={{ mb: 3 }}
                onClick={handleGetUsers}
                disabled={isEmpty(selected)}
              >
                Get Users
              </Button>
            </Box>
          )}

          <Box>
            <TableContainer component={Paper} sx={{ maxHeight: "300px" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Sr #</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Session Host
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Users
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Session Id
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((item: any, index: number) => (
                      <TableRow
                        key={item.userSessionId}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell align="center">
                          {item.sessionHostName}
                        </TableCell>
                        <TableCell align="center">{item.userName}</TableCell>
                        <TableCell align="center">
                          {item.userSessionId}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell align="right">
                        Currently, there are no users.
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>

        {/**************** Right Grid ****************/}
        <Grid item xs={6} sx={{ padding: "20px 50px 20px 50px" }}>
          <form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Box sx={{ marginBottom: "20px" }}>
              <TextField
                fullWidth
                name="messageTitle"
                label="Message Title"
                type="text"
                value={formik.values.messageTitle}
                onChange={formik.handleChange}
                error={
                  formik.touched.messageTitle &&
                  Boolean(formik.errors.messageTitle)
                }
                helperText={
                  formik.touched.messageTitle && formik.errors.messageTitle
                }
              />
            </Box>

            <Box sx={{ marginBottom: "20px" }}>
              <TextField
                fullWidth
                name="messageBody"
                label="Message Body"
                value={formik.values.messageBody}
                onChange={formik.handleChange}
                multiline
                rows={10}
                error={
                  formik.touched.messageBody &&
                  Boolean(formik.errors.messageBody)
                }
                helperText={
                  formik.touched.messageBody && formik.errors.messageBody
                }
              />
            </Box>

            {isSendMessage ? (
              <Box>
                <Button
                  disabled
                  variant="contained"
                  sx={{ width: "200px" }}
                  endIcon={<CircularProgress size={20} />}
                >
                  Send
                </Button>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="contained"
                  sx={{ width: "200px" }}
                  type="submit"
                  disabled={!isFormValid()}
                >
                  Send
                </Button>
              </Box>
            )}
          </form>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Communication;
